/**
 * components/FileDetailModal.jsx
 * Detail modal for a single scan — light theme, fully readable.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import API from '../services/api';
import ResultsTable from './ResultsTable';
import TabularResultsView from './TabularResultsView';
import '../styles/dashboard.css';


/**
 * Append ?token=<jwt> so the protected /api/image/ endpoint accepts requests
 * from <img> tags which cannot set Authorization headers.
 */
function withAuthToken(url) {
  if (!url) return url;
  const jwt = localStorage.getItem('auth_token');
  return jwt ? `${url}?token=${encodeURIComponent(jwt)}` : url;
}

const CAT_LABELS = {
  linear_dimensions:          'Linear Dimensions',
  angular_dimensions:         'Angular Dimensions',
  circular_radial_dimensions: 'Circular / Radial',
  chamfer_bevel_dimensions:   'Chamfer & Bevel',
  coordinate_dimensions:      'Coordinate Dims',
  thread_dimensions:          'Thread Dimensions',
  surface_dimensions:         'Surface Dims',
  taper_slope_dimensions:     'Taper & Slope',
  gdt_feature_control:        'GD&T',
  surface_finish_cosmetic:    'Surface Finish',
  material_metallurgy:        'Material & Metallurgy',
  manufacturing_process:      'Manufacturing Process',
  assembly_fit:               'Assembly & Fit',
  inspection_quality:         'Inspection & Quality',
  regulatory_compliance:      'Regulatory Compliance',
  environmental_durability:   'Environmental',
};

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

function fmtDate(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function Skeleton({ height = 20 }) {
  return <div className="db-skeleton" style={{ height, borderRadius: 8, width: '100%', marginBottom: 8 }} />;
}

function StatusBadge({ status }) {
  const cls = status === 'completed' ? 'db-badge-success'
            : status === 'failed'    ? 'db-badge-error' : 'db-badge-warn';
  return <span className={`db-badge ${cls}`}>{status}</span>;
}

/* ── CC badge ── */
function CcBadge({ value }) {
  if (value === 'Yes') {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: '#ECFDF5', border: '1px solid #6EE7B7',
        color: '#059669', borderRadius: 999,
        padding: '0.18rem 0.55rem', fontSize: '0.72rem', fontWeight: 700,
      }}>● Yes</span>
    );
  }
  return <span style={{ color: '#9C9490', fontSize: '0.78rem', fontWeight: 500 }}>No</span>;
}

/* ── Location chip ── */
function LocationChip({ value }) {
  return (
    <span style={{
      display: 'inline-block',
      background: '#EFF6FF', border: '1px solid #BFDBFE',
      color: '#1D4ED8', borderRadius: 6,
      padding: '0.15rem 0.5rem', fontSize: '0.76rem', fontWeight: 700,
      whiteSpace: 'nowrap',
    }}>
      {value || '—'}
    </span>
  );
}

/* ── Type chip ── */
function TypeChip({ value }) {
  const v = (value || '').toLowerCase();
  let bg = '#F3F4F6', border = '#D1D5DB', color = '#374151';
  if (v === 'dimensional') { bg = '#EFF6FF'; border = '#BFDBFE'; color = '#1D4ED8'; }
  if (v === 'visual')      { bg = '#F5F3FF'; border = '#C4B5FD'; color = '#6D28D9'; }
  if (v === 'functional')  { bg = '#FFF7ED'; border = '#FED7AA'; color = '#C2410C'; }
  return (
    <span style={{
      background: bg, border: `1px solid ${border}`, color,
      borderRadius: 6, padding: '0.15rem 0.5rem',
      fontSize: '0.74rem', fontWeight: 700,
    }}>
      {value || '—'}
    </span>
  );
}

/* ── Limit value ── */
function LimitVal({ value, accent }) {
  if (value == null || value === 'NA' || value === '') {
    return <span style={{ color: '#C4BFBA', fontSize: '0.78rem' }}>—</span>;
  }
  return (
    <span style={{ color: accent, fontWeight: 700, fontSize: '0.82rem', fontFamily: 'monospace' }}>
      {typeof value === 'number' ? value.toFixed(3) : value}
    </span>
  );
}

/* ── ReviewSection ── */
function ReviewSection({ reviewDetails }) {
  if (!reviewDetails || !reviewDetails.has_review) return null;
  const status = reviewDetails.approval_status || reviewDetails.review_status;
  const isApproved = status === 'approved';
  const isRejected = status === 'rejected';
  if (!isApproved && !isRejected) return null;

  const reviewerLine = reviewDetails.reviewer_name
    ? `${reviewDetails.reviewer_name}${reviewDetails.reviewer_email ? ` (${reviewDetails.reviewer_email})` : ''}`
    : reviewDetails.reviewer_email || '—';

  return (
    <div style={{
      margin: '16px 0',
      padding: '14px 18px',
      borderRadius: 12,
      border: `1px solid ${isApproved ? '#6EE7B7' : '#FCA5A5'}`,
      background: isApproved ? '#F0FDF4' : '#FEF2F2',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: reviewDetails.comments ? 8 : 0 }}>
        <span style={{
          display: 'inline-block',
          padding: '2px 12px',
          borderRadius: 20,
          fontSize: '0.74rem',
          fontWeight: 700,
          background: isApproved ? '#D1FAE5' : '#FEE2E2',
          color: isApproved ? '#065F46' : '#991B1B',
        }}>
          {isApproved ? '✓ Approved' : '✕ Rejected'}
        </span>
        <span style={{ fontSize: '0.8rem', color: '#5C5450' }}>
          {isApproved ? 'Approved' : 'Rejected'} by <strong>{reviewerLine}</strong>
        </span>
      </div>
      {reviewDetails.comments && (
        <div style={{ marginTop: 8 }}>
          <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#9C9490', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Reviewer Comment
          </span>
          <p style={{ margin: '4px 0 0', fontSize: '0.84rem', color: '#1A1410', lineHeight: 1.55, fontStyle: 'italic' }}>
            "{reviewDetails.comments}"
          </p>
        </div>
      )}
    </div>
  );
}

/* ══ MODAL ══════════════════════════════════════════════════════ */
export default function FileDetailModal({ fileId, onClose, onDownload }) {
  const [data,           setData]           = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState(null);
  const [search,         setSearch]         = useState('');
  const [downloading,    setDownloading]    = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadOpen,   setDownloadOpen]   = useState(false);
  const [activeTab,      setActiveTab]      = useState('table');
  const [ccFilter,       setCcFilter]       = useState('all');
  const [reviewDetails,  setReviewDetails]  = useState(null);
  const downloadRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const data = await API.getResults(fileId);
      setData(data);
    } catch (e) { setError(e.error || 'Failed to load scan details'); }
    finally { setLoading(false); }
  }, [fileId]);

  useEffect(() => {
    if (!fileId) return;
    API.getDrawingReviewDetails(fileId)
      .then(setReviewDetails)
      .catch(() => {});
  }, [fileId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const handleDownload = async () => {
    if (!data) return;
    setDownloading(true);
    const partNo = data.metadata?.part_no;
    const base = (partNo && String(partNo).trim())
      ? String(partNo).trim().replace(/[\\/:*?"<>|]/g, '_')
      : data.file_name.replace(/\.[^.]+$/, '');
    try { await onDownload(fileId, `${base} - Inspection Plan.xlsx`); }
    finally { setDownloading(false); }
  };

  const handleDownloadPdf = async () => {
    if (!data) return;
    setDownloadingPdf(true);
    const partNo = data.metadata?.part_no;
    const base = (partNo && String(partNo).trim())
      ? String(partNo).trim().replace(/[\\/:*?"<>|]/g, '_')
      : data.file_name.replace(/\.[^.]+$/, '');
    try { await API.downloadPdf(fileId, `${base} - Inspection Plan.pdf`); }
    finally { setDownloadingPdf(false); }
  };

  // Close download dropdown when clicking outside
  useEffect(() => {
    if (!downloadOpen) return;
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [downloadOpen]);

  const filteredItems = (data?.inspection_items ?? []).filter((item) => {
    const matchesSearch =
      !search ||
      String(item.specification || '').toLowerCase().includes(search.toLowerCase()) ||
      String(item.location_on_drawing || '').toLowerCase().includes(search.toLowerCase()) ||
      String(item.inspection_tool || '').toLowerCase().includes(search.toLowerCase()) ||
      String(item.gdt || '').toLowerCase().includes(search.toLowerCase());
    const matchesCC =
      ccFilter === 'all' ||
      (ccFilter === 'yes' && item.critical_characteristic === 'Yes') ||
      (ccFilter === 'no'  && item.critical_characteristic !== 'Yes');
    return matchesSearch && matchesCC;
  });

  const ccCount = (data?.inspection_items ?? []).filter(i => i.critical_characteristic === 'Yes').length;

  return (
    <div className="db-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="db-modal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="db-modal-header">
          <div style={{ minWidth: 0 }}>
            <div className="db-modal-title">
              {loading ? 'Loading...' : (data?.file_name ?? `Scan #${fileId}`)}
            </div>
            <div className="db-modal-sub">
              {data && (
                <>Uploaded {fmtDate(data.upload_timestamp)}
                  {data.completed_timestamp && ` · Completed ${fmtDate(data.completed_timestamp)}`}
                </>
              )}
            </div>
          </div>
          <div className="db-modal-actions">
            {data?.status === 'completed' && (
              <div style={{ position: 'relative' }} ref={downloadRef}>
                <button
                  className="db-action-btn db-action-download"
                  onClick={() => setDownloadOpen(o => !o)}
                  disabled={downloading || downloadingPdf}
                >
                  {(downloading || downloadingPdf) ? 'Downloading…' : '↓ Download Report ▾'}
                </button>
                {downloadOpen && (
                  <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 170, overflow: 'hidden',
                  }}>
                    <button
                      style={{
                        display: 'block', width: '100%', padding: '9px 16px',
                        textAlign: 'left', background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: 13, color: '#1e293b',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                      onClick={() => { setDownloadOpen(false); handleDownload(); }}
                    >
                      Excel (.xlsx)
                    </button>
                    <button
                      style={{
                        display: 'block', width: '100%', padding: '9px 16px',
                        textAlign: 'left', background: 'none', border: 'none',
                        cursor: 'pointer', fontSize: 13, color: '#1e293b',
                      }}
                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                      onClick={() => { setDownloadOpen(false); handleDownloadPdf(); }}
                    >
                      PDF (.pdf)
                    </button>
                  </div>
                )}
              </div>
            )}
            {data && <StatusBadge status={data.status} />}
            <button className="db-modal-close" onClick={onClose} aria-label="Close">✕</button>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="db-modal-body">

          {/* Loading */}
          {loading && (
            <div style={{ padding: '24px 0' }}>
              {[...Array(5)].map((_, i) => <Skeleton key={i} height={40} />)}
            </div>
          )}

          {/* Error */}
          {error && !loading && (
            <div className="db-modal-error">
              <p>{error}</p>
              <button className="db-btn-ghost" style={{ marginTop: 12 }} onClick={fetchData}>Retry</button>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {/* ── Metadata grid ── */}
              {data.metadata && (
                <div>
                  <div className="db-modal-section-title">Drawing Metadata</div>
                  <div className="db-modal-meta-grid">
                    {[
                      ['Part No',    data.metadata.part_no],
                      ['Drawing No', data.metadata.drawing_no],
                      ['Part Name',  data.metadata.part_name],
                      ['Rev No',     data.metadata.rev_no],
                    ].map(([label, value]) => (
                      <div key={label} className="db-modal-meta-card">
                        <label>{label}</label>
                        <span style={{ color: '#1A1410', fontWeight: 700, fontSize: '0.92rem' }}>{value || '—'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Stats ── */}
              <div className="db-modal-stats-row">
                <div className="db-modal-stat">
                  <div className="db-modal-stat-val" style={{ color: '#F97316' }}>{data.cc_count ?? 0}</div>
                  <div className="db-modal-stat-lbl">CC Symbols</div>
                </div>
                <div className="db-modal-stat">
                  <div className="db-modal-stat-val" style={{ color: '#2563EB' }}>{data.item_count ?? 0}</div>
                  <div className="db-modal-stat-lbl">Inspection Items</div>
                </div>
                <div className="db-modal-stat">
                  <div className="db-modal-stat-val" style={{ color: '#059669' }}>{ccCount}</div>
                  <div className="db-modal-stat-lbl">Critical Items</div>
                </div>
              </div>

              {/* ── Review info ── */}
              <ReviewSection reviewDetails={reviewDetails} />

              {/* ── Tabs ── */}
              <div className="tabs-row" style={{ marginBottom: 0 }}>
                <button
                  className={`tab-button ${activeTab === 'table' ? 'active' : ''}`}
                  onClick={() => setActiveTab('table')}
                >
                  Inspection Plan
                </button>
                {data.annotated_image_path && (
                  <button
                    className={`tab-button ${activeTab === 'drawing' ? 'active' : ''}`}
                    onClick={() => setActiveTab('drawing')}
                  >
                    Original Drawing
                  </button>
                )}
                <button
                  className={`tab-button ${activeTab === 'scan-info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('scan-info')}
                >
                  ⏱ Scan Info
                </button>
              </div>

              {/* ── Inspection Table ── */}
              {activeTab === 'table' && (
                (data.scan_type === 'tabular' || (Array.isArray(data.variants) && data.variants.length > 0)) ? (
                  <TabularResultsView fileId={fileId} variants={data.variants || []} />
                ) : (
                  <ResultsTable
                    metadata={data.metadata}
                    items={data.inspection_items}
                    notesItems={data.notes_items || []}
                    visualInspectionItems={data.visual_inspection_items}
                    safetyHazardItems={data.safety_hazard_items || []}
                    gdtTableRows={data.gdt_table_rows || []}
                    gdtSymbolsUnused={data.gdt_symbols_unused || []}
                    lowConfidenceDimensions={data.low_confidence_dimensions || []}
                    ccCount={data.cc_count}
                    itemCount={data.item_count}
                    fileId={fileId}
                    onSaved={fetchData}
                    onPromoteLowConfidence={async (lcIndex, destination, overrides) => {
                      await API.promoteResultsRow(fileId, lcIndex, destination, overrides);
                      await fetchData();
                    }}
                    onDemoteDimension={async (specification, location) => {
                      await API.demoteResultsRow(fileId, specification, location);
                      await fetchData();
                    }}
                    onDemoteNote={async (noteLabel, category) => {
                      await API.demoteResultsNote(fileId, noteLabel, category);
                      await fetchData();
                    }}
                    onDemoteGdt={async (gdtItemNo) => {
                      await API.demoteResultsGdt(fileId, gdtItemNo);
                      await fetchData();
                    }}
                    blockTolerances={data.block_tolerances ?? {}}
                    scanDurationMs={data.scan_duration_ms}
                  />
                )
              )}

              {/* ── Scan Info ── */}
              {activeTab === 'scan-info' && (() => {
                const upload = data.upload_timestamp ? toUTC(data.upload_timestamp) : null;
                const completed = data.completed_timestamp ? toUTC(data.completed_timestamp) : null;
                const durationMs = upload && completed ? completed - upload : null;
                const fmtDuration = (ms) => {
                  if (!ms || ms < 0) return '—';
                  const s = Math.round(ms / 1000);
                  if (s < 60) return `${s} seconds`;
                  const m = Math.floor(s / 60);
                  return `${m}m ${s % 60}s`;
                };
                const fmtDate = (d) => d ? d.toLocaleString() : '—';
                const ccSymbolRaw = data.cc_symbol || 'spade';
                const ccSymbolList = ccSymbolRaw.split(',').map(s => s.trim()).filter(Boolean);
                const CC_LABELS = {
                  spade: 'Spade (♠)', club: 'Club (♣)',
                  non_filled_square: 'Non-Filled Square (□)', filled_square: 'Filled Square (■)',
                  non_filled_circle: 'Non-Filled Circle (○)', circle_filled: 'Filled Circle (●)',
                  non_filled_triangle: 'Non-Filled Triangle (△)', filled_triangle: 'Filled Triangle (▲)',
                  non_filled_diamond: 'Non-Filled Diamond (◇)', filled_diamond: 'Filled Diamond (◆)',
                  oblong: 'Oblong (⬭)',
                };
                const infoRows = [
                  { label: 'Total Scan Time', value: fmtDuration(durationMs), highlight: true },
                  { label: 'Scan Started', value: fmtDate(upload) },
                  { label: 'Scan Completed', value: fmtDate(completed) },
                  { label: 'File Name', value: data.file_name || '—' },
                  { label: 'Part Number', value: data.metadata?.part_no || '—' },
                  { label: 'Drawing Number', value: data.metadata?.drawing_no || '—' },
                  { label: 'CC Symbols Selected', value: ccSymbolList.map(k => CC_LABELS[k] || k).join(', ') || '—' },
                  { label: 'Inspection Items', value: data.item_count != null ? String(data.item_count) : '—' },
                  { label: 'CC Items Found', value: data.cc_count != null ? String(data.cc_count) : '—' },
                ];
                return (
                  <div style={{ padding: '20px 4px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                      {infoRows.map(row => (
                        <div key={row.label} style={{
                          display: 'flex', alignItems: 'center', gap: 16,
                          padding: '12px 18px',
                          background: row.highlight ? '#FFF3E0' : '#FAFAF9',
                          border: row.highlight ? '1.5px solid #FDB976' : '1px solid var(--border)',
                          borderRadius: 10,
                        }}>
                          <span style={{ minWidth: 160, fontSize: 12, fontWeight: 600, color: '#78350f', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {row.label}
                          </span>
                          <span style={{ fontSize: row.highlight ? 18 : 14, fontWeight: row.highlight ? 700 : 400, color: row.highlight ? '#b45309' : '#1e293b', fontFamily: row.highlight ? 'inherit' : 'monospace' }}>
                            {row.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── Original Drawing — native PDF viewer, same as the results tab ── */}
              {activeTab === 'drawing' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <a
                      href={withAuthToken(`/api/image/${fileId}/pdf`) + '&download=1'}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '7px 14px', borderRadius: 6, background: '#f0f9ff',
                        border: '1px solid #bae6fd', color: '#0369a1', fontWeight: 600,
                        fontSize: 13, textDecoration: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      ⬇ Download PDF
                    </a>
                  </div>
                  <iframe
                    title="Original drawing"
                    src={withAuthToken(`/api/image/${fileId}/pdf`)}
                    style={{
                      width: '100%',
                      height: '65vh',
                      border: '1px solid var(--border)',
                      borderRadius: 12,
                      background: '#F7F4F0',
                    }}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
